import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const FourDays = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">4 DAYS TANZANIA WILDLIFE SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          4 Days Tanzania Wildlife Safari – Best of Tarangire, Ngorongoro and Lake Manyara National Parks
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on a captivating <strong>4 days Tanzania wildlife safari</strong> to explore the highlights of Tanzania's Northern Safari Circuit. Immerse yourself in the awe-inspiring wildlife amidst diverse landscapes, open savannas in the forest, large baobab trees, elephants roaming through the bush, and an enchanting array of animals within a collapsed volcano.
              </p>
            </div>

            {/* Placeholder for Safari Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/55.jpg" 
                alt="3 Days Tanzania Wildlife Safari" 
                className="w-full h-48 object-cover"
              />
                
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Tanzania Wildlife Safari - Antelopes in savanna
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 4 Days Tanzania Wildlife Safari
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival | Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  The company representative will greet you at the airport and transport you to your accommodation in Arusha. Depending on your arrival time, you can enjoy a city tour around Arusha and gear up for the adventure of a lifetime. Bustling Arusha is a pivotal hub in northern Tanzania's safari experience, with most travelers passing through at some point.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Gran Melia Arusha
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Transfer to Tarangire National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, your safari driver guide will pick you up from your accommodation and drive you to Tarangire National Park, a journey of about two and a half hours to well-paved roads. The park, named after the Tarangire River, serves as the only water source for wildlife during the dry season. The landscape is dotted with distinctive baobab trees, and you can expect to spot large elephant herds, often seen gathering by the river, as well as giraffes, bushbucks, and hartebeests. Predators like lions and leopards frequently follow these animals for easy prey, while the trees are adorned with numerous bird species like bee-eaters, hornbills, and ground hornbills, enhancing your wildlife experience. Some Baobab trees, some over a thousand years old.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Kubu Kubu Tented Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Transfer to Ngorongoro Crater</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, you will drive into the crater regions of the Ngorongoro Conservation Area. As you approach Ngorongoro Crater, it appears as a forgotten world hidden by steep volcanic walls in a pristine wilderness featuring sweeping savanna, pockets of acacia woodland, and glimmering lakes and swamps. The Crater formed by a massive volcanic explosion and collapse two to three million years ago, is a breathtaking natural amphitheater and a UNESCO World Heritage Site, hosting some of Africa's densest large mammal populations. Descending into the Crater, you will encounter large herds of wildebeest and zebra, impala, and gazelle.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Kitela Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Lake Manyara National Park & Transfer back to Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, you will drive to Lake Manyara National Park, a small but diverse park located 120 km west of Arusha. This park offers unique experiences for nature enthusiasts, including the towering Great Rift Escarpment on its western boundary. The park's namesake is a shallow salt lake, which floods and dries with the seasons, attracting thousands of wading birds, including flamingos and over 400 other bird species. In the park's dense evergreen forests, you may spot baboons and elephants, and be on the lookout for the famous tree-climbing lions.
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  You will enjoy a picnic lunch after your game drive, you will bid farewell to your guide and be dropped off at your chosen location, whether it be the Arusha Kilimanjaro Airport, or Arusha Airport.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: None (Transfer back to Arusha)
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transportation via 4x4 safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodation and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Entrance fees to all National Parks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    En-route lunches
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Game drives as per itinerary
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa Fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Laundry
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic drinks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Health test requirements
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Tanzania Wildlife Safari</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 4 Days, 3 Nights safari
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Regions / Parks
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Hotel pickup - Your side returned to your hotel
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FourDays;